import React from "react";
import styled from "styled-components";
import WhiteCheck from "../../assets/icons/white_check.svg";
import stepperContent from "../../translations/enUS/BillingAndPayment.en-US.json";
import { Colors } from "../../constants/Colors";
import { BREAKPOINTS } from "../../constants/appConstants";
import { revealLoginRedirectionLink } from "../../util/helpURLNavigation";
import RevealLink from "../common/RevealLink";
import PropTypes from "prop-types";

const { paymentStepperContents, postPaymentStepperContentA11y, beforePaymentStepperContentA11y } = stepperContent;

// Constants for line heights

const HEIGHTS = {
  MOBILE: 45,
  TABLET: 25,
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
  height: auto;
  flex: none;
  order: 5;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    flex-direction: column;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  height: auto;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;
  height: auto;
  flex: none;
  align-self: stretch;
  flex-grow: 1;
  width: 33.33%;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    display: flex;
    flex-direction: row;
    width: unset;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
  height: auto;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    position: relative;
  }
`;

const Spacer = styled.div`
  width: auto;
  height: auto;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 1;
  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    display: none;
  }
`;

const Spacer1 = styled.div`
  width: auto;
  height: auto;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 1;
`;

const getCircleBackground = (isFromOrangeBanner, content) => {
  if (isFromOrangeBanner && content === "1") return Colors.black;
  if (!isFromOrangeBanner && content === "3") return Colors.black;
  if (isFromOrangeBanner && content !== "1") return "none";
  return Colors.green;
};

const Circle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  width: 24px;
  height: 24px;
  background: ${(props) => getCircleBackground(props.isFromOrangeBanner, props.content)};
  border-radius: 100px;
  flex: none;
  order: 1;
  flex-grow: 0;
  position: relative;
  border: ${(props) => (props.isFromOrangeBanner ? `1px solid ${Colors.black}` : "none")};

  &::before {
    content: ${(props) => `'${props.content}'`};
    display: ${(props) => (props.isFromOrangeBanner ? "flex" : "block")};
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    font-family: "Neue Haas Grotesk Text Std";
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    line-height: 15px;
    text-align: center;
    color: ${(props) => (props.isFromOrangeBanner && props.content !== "1" ? Colors.black : Colors.white)};
    background: ${(props) => (props.content ? "none" : `url(${WhiteCheck}) center / contain no-repeat`)};
  }

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    align-self: flex-start;
    order: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0px;
    width: 24px;
    height: 24px;
    border-radius: 100px;
    flex: none;
    order: 0;
    flex-grow: 0;
  }
`;

const Line = styled.div`
  height: 1px;
  background: ${(props) => (props.isFromOrangeBanner ? Colors.black : Colors.green)};
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    height: 100%;
    width: 1px;
  }
`;

const LineBase = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: auto;
  height: auto;
  flex: none;
  align-self: stretch;
  flex-grow: 1;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    display: none;
  }
`;

const HorizontalLine = styled(LineBase)`
  order: ${(props) => props.order || 2};
`;

const HorizontalLine1 = styled(LineBase)`
  order: 0;
`;

const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 4px;
  gap: 8px;
  width: 100%;
  height: auto;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    display: none;
  }
`;

const Label = styled.div`
  width: auto;
  height: auto;
  font-family: "Neue Haas Grotesk Text Std";
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  color: ${Colors.black};
  order: 0;
  flex-grow: 1;
`;

const StepContent = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 3px 0px 16px;
    gap: 8px;
  }
`;

const StepLabel = styled.div`
  font-family: "Neue Haas Grotesk Text Std";
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.black};
  flex: none;
  order: 0;
  text-align: left;
`;

const StepDescription = styled.div`
  font-family: "Neue Haas Grotesk Text Std";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.black};
  flex: none;
  order: 1;
  align-self: flex-start;
  flex-grow: 0;
  text-align: left;
`;

const StepDescContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  width: 100%;
  height: auto;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    display: none;
  }
`;

const DesktopStepperDescription = styled.div`
  width: 33.33%;
  height: auto;
  font-family: "Neue Haas Grotesk Text Std";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${Colors.black};
  flex-grow: 1;

  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    text-align: left;
    width: auto;
  }
`;

const ClickableText = styled.span`
  cursor: pointer;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  text-underline-offset: 3px;
`;

const VerticalLine = styled.div`
  display: none;
  width: 1px;
  background: ${(props) => (props.isFromOrangeBanner ? Colors.black : Colors.green)};
  flex-grow: 1;
  align-self: stretch;

  /* Adjust height for mobile view between 418px and 534px when adjustHeight is true */
  ${({ stepOne }) => `
  ${
    stepOne
      ? `
    @media (min-width: 418px) and (max-width: 534px) {
      height: ${HEIGHTS.TABLET}px !important;
    }
  `
      : `
    @media (min-width: 481px) and (max-width: 534px) {
      height: ${HEIGHTS.MOBILE}px !important;
    }
  `
  }
`}

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    display: block;
    height: ${HEIGHTS.MOBILE}px;
  }

  @media (min-width: ${BREAKPOINTS.MOBILE + 1}px) and (max-width: ${BREAKPOINTS.TABLET}px) {
    display: block;
    height: ${HEIGHTS.TABLET}px;
  }
`;

const VerticalLineWrapper = styled.div`
  display: none;
  @media (max-width: ${BREAKPOINTS.TABLET}px) {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 0px 0px 0px 12px;
    gap: 8px;
    width: 24px;
    height: auto;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 1;
    top: 36px;
    position: absolute;
  }
`;

const handleKeyDown = (e, makePayment, invoiceNumber) => {
  if (e.key === "Enter") makePayment("makepayment", invoiceNumber);
};
const PaymentStepper = ({ t, isFromOrangeBanner, makePayment, invoiceNumber }) => {
  const a11yContent = isFromOrangeBanner ? beforePaymentStepperContentA11y : postPaymentStepperContentA11y;

  return (
    <Container>
      <Header>
        <Step order={0}>
          <Row>
            <Spacer />
            <Circle
              isFromOrangeBanner={isFromOrangeBanner}
              content={isFromOrangeBanner ? "1" : ""}
              aria-label={t(a11yContent.makePayment)}
            />
            {/* StepContent for Mobile View */}
            <StepContent>
              <StepLabel>{t(paymentStepperContents.makePayment)}</StepLabel>
              {isFromOrangeBanner ? (
                <StepDescription>
                  {t(paymentStepperContents.youCanPayByClicking)}
                  <ClickableText
                    onClick={() => makePayment("makepayment", invoiceNumber)}
                    aria-label={t(beforePaymentStepperContentA11y.makeAPaymentAriaLabel)}
                    id="make-a-payment-orangeBanner"
                    tabIndex="0"
                    onKeyDown={(e) => handleKeyDown(e, makePayment, invoiceNumber)}
                  >
                    {t(paymentStepperContents.makeAPayment)}
                  </ClickableText>
                  .
                </StepDescription>
              ) : (
                <StepDescription>{t(paymentStepperContents.payByClicking)}</StepDescription>
              )}
            </StepContent>
            <HorizontalLine order={0}>
              <Line isFromOrangeBanner={isFromOrangeBanner} />
            </HorizontalLine>
            <VerticalLineWrapper>
              <VerticalLine isFromOrangeBanner={isFromOrangeBanner} stepOne={true} />
            </VerticalLineWrapper>
          </Row>
          <LabelRow>
            <Label>{t(paymentStepperContents.makePayment)}</Label>
          </LabelRow>
        </Step>

        <Step order={1}>
          <Row>
            <HorizontalLine1>
              <Line isFromOrangeBanner={isFromOrangeBanner} />
            </HorizontalLine1>

            <Circle
              isFromOrangeBanner={isFromOrangeBanner}
              content={isFromOrangeBanner ? "2" : ""}
              aria-label={t(a11yContent.accountTemporarilyRestored)}
            />
            {/* StepContent for Mobile View */}
            <StepContent>
              <StepLabel>{t(paymentStepperContents.accountTemporarilyRestored)}</StepLabel>
              {isFromOrangeBanner ? (
                <StepDescription>
                {t(paymentStepperContents.paymentMayTakeWithReveal)}
              </StepDescription>
              ) : (
                <StepDescription>
                  {t(paymentStepperContents.paymentMayTake)}&nbsp;
                  <RevealLink
                    onClick={() => window.open(revealLoginRedirectionLink(), "_blank", "noopener,noreferrer")}
                  />
                  .
                </StepDescription>
              )}
            </StepContent>
            <HorizontalLine>
              <Line isFromOrangeBanner={isFromOrangeBanner} />
            </HorizontalLine>
            <VerticalLineWrapper>
              <VerticalLine isFromOrangeBanner={isFromOrangeBanner} stepOne={false} />
            </VerticalLineWrapper>
          </Row>
          {/* LabelRow for Desktop View */}
          <LabelRow>
            <Label>{t(paymentStepperContents.accountTemporarilyRestored)}</Label>
          </LabelRow>
        </Step>
        <Step order={2}>
          <Row>
            <HorizontalLine1>
              <Line isFromOrangeBanner={isFromOrangeBanner} />
            </HorizontalLine1>

            <Circle isFromOrangeBanner={isFromOrangeBanner} content="3" aria-label={t(a11yContent.accountFullyRestored)} />
            {/* StepContent for Mobile View */}
            <StepContent>
              <StepLabel>{t(paymentStepperContents.accountFullyRestored)}</StepLabel>
              <StepDescription>{t(paymentStepperContents.restoredNextWorkingDay)}</StepDescription>
            </StepContent>
            <Spacer1 />
          </Row>
          <LabelRow>
            <Label>{t(paymentStepperContents.accountFullyRestored)}</Label>
          </LabelRow>
        </Step>
      </Header>
      <StepDescContainer>
        {isFromOrangeBanner ? (
          <DesktopStepperDescription>
            {t(paymentStepperContents.youCanPayByClicking)}
            <ClickableText
              onClick={() => makePayment("makepayment", invoiceNumber)}
              aria-label={t(beforePaymentStepperContentA11y.makeAPaymentAriaLabel)}
              id="make-a-payment-orangeBanner"
              tabIndex="0"
              onKeyDown={(e) => handleKeyDown(e, makePayment, invoiceNumber)}
            >
              {t(paymentStepperContents.makeAPayment)}
            </ClickableText>
            .
          </DesktopStepperDescription>
        ) : (
          <DesktopStepperDescription>{t(paymentStepperContents.payByClicking)}</DesktopStepperDescription>
        )}
        {isFromOrangeBanner ? (
          <DesktopStepperDescription>
          {t(paymentStepperContents.paymentMayTakeWithReveal)}
        </DesktopStepperDescription>
        ) : (
          <DesktopStepperDescription>
            {t(paymentStepperContents.paymentMayTake)}&nbsp;
            <RevealLink
              onClick={() => window.open(revealLoginRedirectionLink(), "_blank", "noopener,noreferrer")}
              />
            .
          </DesktopStepperDescription>
        )}
        <DesktopStepperDescription>{t(paymentStepperContents.restoredNextWorkingDay)}</DesktopStepperDescription>
      </StepDescContainer>
    </Container>
  );
};
PaymentStepper.propTypes = {
  t: PropTypes.func.isRequired,
  isFromOrangeBanner: PropTypes.bool.isRequired,
  makePayment: PropTypes.func.isRequired,
  invoiceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PaymentStepper;
